import { cn, Tooltip } from "@gotts.ai/ui";
import { AccountBar } from "@gotts.ai/ui/devtools/components/AccountBar";
import { GroupedSideNav } from "@gotts.ai/ui/devtools/components/GroupedSideNav";
import { useDeployment } from "@gotts.ai/ui/devtools/context";
import { useActivityCounts } from "@gotts.ai/ui/devtools/hooks/useActivityCounts";
import { useSidebarState } from "@gotts.ai/ui/devtools/hooks/useSidebarState";
import {
  BASE_GROUPS,
  BASE_VIEWS,
  type ViewDef,
} from "@gotts.ai/ui/devtools/nav";
import { Database, FlaskConical, Radio, Square } from "lucide-react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { TevmBackendProvider } from "./context/TevmBackendProvider.js";
import { TevmContext } from "./context/TevmContext.js";
import { WebMcpProvider } from "./context/WebMcpProvider.js";
import { useDeploymentProgress } from "./hooks/useDeploymentProgress.js";
import { AppRoutes } from "./router.js";
import { blockTimeModeToInterval } from "./runtime/environment-options.js";
import { Landing } from "./views/Landing.js";

import type { TevmContextValue } from "./context/TevmContext.js";
import type { BlockTimeMode } from "./runtime/environment-options.js";
import type { UniswapDeployment } from "@gotts.ai/devenv";
import type { Chain, PublicClient, TestClient, WalletClient } from "viem";

// ---------------------------------------------------------------------------
// Navigation definitions — base views + webenv-specific views
// ---------------------------------------------------------------------------

const WEBENV_ONLY_VIEWS: ViewDef[] = [
  {
    id: "scenarios",
    label: "Scenarios",
    path: "/scenarios",
    group: "Webenv",
    icon: FlaskConical,
  },
  {
    id: "state",
    label: "State",
    path: "/state",
    group: "Webenv",
    icon: Database,
  },
  {
    id: "webmcp",
    label: "WebMCP",
    path: "/webmcp",
    group: "Webenv",
    icon: Radio,
  },
];

const GROUPS = [...BASE_GROUPS, "Webenv"] as const;
const VIEWS: ViewDef[] = [...BASE_VIEWS, ...WEBENV_ONLY_VIEWS];

// ---------------------------------------------------------------------------
// Main layout (rendered after Landing completes)
// ---------------------------------------------------------------------------

function AppLayout({ actions }: { actions?: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const sidebar = useSidebarState();
  const { deployment } = useDeployment();
  const {
    txCount,
    eventCount,
    loading: activityLoading,
  } = useActivityCounts(deployment?.blockNumber);
  const badges = { txlog: txCount, events: eventCount };
  const loadingBadges = { txlog: activityLoading, events: activityLoading };

  // Keyboard shortcuts: press 1-9 to switch nav sections
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      const num = parseInt(e.key);
      const view = num >= 1 && num <= VIEWS.length ? VIEWS[num - 1] : undefined;
      if (view) {
        sidebar.closeDrawer();
        void navigate(view.path);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [navigate, sidebar]);

  return (
    <div className="flex h-screen bg-[var(--color-black)] text-[var(--color-text-dim)]">
      {/* Desktop / tablet: inline sidebar */}
      {!sidebar.isMobile && (
        <GroupedSideNav
          groups={GROUPS}
          views={VIEWS}
          collapsed={sidebar.collapsed}
          badges={badges}
          loadingBadges={loadingBadges}
        />
      )}

      {/* Mobile: overlay drawer + backdrop */}
      {sidebar.showDrawer && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/60"
            onClick={sidebar.closeDrawer}
          />
          <div className="fixed inset-y-0 left-0 z-50 w-60">
            <GroupedSideNav
              groups={GROUPS}
              views={VIEWS}
              collapsed={false}
              onItemClick={sidebar.closeDrawer}
              badges={badges}
              loadingBadges={loadingBadges}
            />
          </div>
        </>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        <AccountBar
          onMenuToggle={sidebar.toggle}
          showHamburger={sidebar.isMobile}
          showCollapseToggle={!sidebar.isMobile}
          sidebarCollapsed={sidebar.collapsed}
          actions={actions}
        />
        <main
          className={cn(
            "flex-1 overflow-auto",
            sidebar.isMobile
              ? "px-3 py-3"
              : sidebar.collapsed
                ? "px-5 py-4"
                : "px-8 py-6",
          )}
        >
          <div
            className="content-constrained animate-page-in"
            key={location.pathname}
          >
            <AppRoutes />
          </div>
        </main>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root App: Landing -> TevmBackendProvider -> Layout
// ---------------------------------------------------------------------------

export function App() {
  const deployProgress = useDeploymentProgress();
  const [tevmCtx, setTevmCtx] = useState<TevmContextValue | null>(null);
  const [showLanding, setShowLanding] = useState(true);
  const workerRef = useRef<Worker | null>(null);

  const handleDeployComplete = useCallback(
    (
      clients: {
        publicClient: PublicClient;
        walletClient: WalletClient;
        testClient: TestClient;
        worker: Worker;
        chain: Chain;
      },
      deployment: UniswapDeployment,
      blockTime: BlockTimeMode,
    ) => {
      workerRef.current = clients.worker;

      // Apply block time mining mode
      const interval = blockTimeModeToInterval(blockTime);
      if (interval > 0) {
        void clients.testClient.request({
          method: "evm_setIntervalMining" as never,
          params: [{ interval }] as never,
        });
      }

      setTevmCtx({
        publicClient: clients.publicClient,
        walletClient: clients.walletClient,
        testClient: clients.testClient,
        chain: clients.chain,
        deployment,
        deploymentProgress: deployProgress.progress,
      });
      // Delay hiding the landing page to allow fade transition
      setTimeout(() => {
        setShowLanding(false);
      }, 400);
    },
    [deployProgress.progress],
  );

  const handleStopChain = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
    deployProgress.reset();
    setTevmCtx(null);
    setShowLanding(true);
  }, [deployProgress]);

  // Landing page renders before Tevm is ready
  if (!tevmCtx) {
    return (
      <Landing
        progress={deployProgress}
        onDeployComplete={handleDeployComplete}
      />
    );
  }

  // Show landing during fade-out transition
  if (showLanding) {
    return (
      <>
        <Landing
          progress={deployProgress}
          onDeployComplete={handleDeployComplete}
        />
        {/* Black fade-out overlay */}
        <div
          className="fixed inset-0 z-50"
          style={{
            background: "var(--color-black)",
            animation: "fadeOut 400ms ease-out forwards",
            pointerEvents: "none",
          }}
        />
      </>
    );
  }

  const stopButton = (
    <Tooltip content="Stop chain">
      <button
        onClick={handleStopChain}
        className="rounded-full p-1.5 text-white/30 transition-colors hover:bg-red-500/10 hover:text-red-400"
        aria-label="Stop chain"
      >
        <Square size={14} />
      </button>
    </Tooltip>
  );

  return (
    <TevmContext.Provider value={tevmCtx}>
      <TevmBackendProvider>
        <WebMcpProvider>
          <AppLayout actions={stopButton} />
        </WebMcpProvider>
      </TevmBackendProvider>
    </TevmContext.Provider>
  );
}
